import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import Pager from "@/pages/admin/Pager";
import { Ticket, ArrowDownRight, RotateCcw, Download, Search } from "lucide-react";

/**
 * VouchersTab — admin view of every Hubble Coin API transaction (voucher
 * redemptions & reversals). Reads from `/admin/hubble-coin-txns`.
 *
 * Columns: User (name/phone/role), Reference, Type, Coins, Balance after,
 * Reversed?, Timestamp.
 */
export default function VouchersTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ debit_count: 0, debit_coins: 0, reverse_count: 0, reverse_coins: 0, net_debit_coins: 0 });
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [reversedOnly, setReversedOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit, skip };
    if (q.trim()) params.q = q.trim();
    if (typeFilter !== "all") params.type = typeFilter;
    if (reversedOnly) params.reversed_only = true;
    if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
    if (dateTo) params.date_to = new Date(dateTo + "T23:59:59").toISOString();
    try {
      const { data } = await api.get("/admin/hubble-coin-txns", { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load voucher redemptions");
    } finally {
      setLoading(false);
    }
  }, [limit, skip, q, typeFilter, reversedOnly, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  // Reset pagination whenever a filter changes
  useEffect(() => { setSkip(0); }, [q, typeFilter, reversedOnly, dateFrom, dateTo]);

  const exportCsv = async () => {
    // Fetch up to 2000 rows honouring current filters (excluding pagination)
    const params = { limit: 2000, skip: 0 };
    if (q.trim()) params.q = q.trim();
    if (typeFilter !== "all") params.type = typeFilter;
    if (reversedOnly) params.reversed_only = true;
    if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
    if (dateTo) params.date_to = new Date(dateTo + "T23:59:59").toISOString();
    try {
      const { data } = await api.get("/admin/hubble-coin-txns", { params });
      const headers = ["timestamp", "type", "user_name", "user_phone", "user_role", "reference_id", "coins", "balance_after", "reversed"];
      const escape = (v) => {
        if (v == null) return "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      };
      const lines = [headers.join(",")];
      for (const r of data.items || []) {
        lines.push([
          r.created_at, r.type, r.user_name, r.user_phone, r.user_role,
          r.reference_id, r.amount, r.points_after, r.reversed ? "yes" : "no",
        ].map(escape).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `voucher-redemptions-${format(new Date(), "yyyyMMdd-HHmm")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${(data.items || []).length} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to export");
    }
  };

  const summaryCards = useMemo(() => ([
    { key: "debits", label: "Voucher redemptions", value: summary.debit_count, sub: `${summary.debit_coins} coins debited`, icon: ArrowDownRight, tint: "text-pink-300" },
    { key: "reversals", label: "Reversals", value: summary.reverse_count, sub: `${summary.reverse_coins} coins refunded`, icon: RotateCcw, tint: "text-amber-300" },
    { key: "net", label: "Net coins spent", value: summary.net_debit_coins, sub: "= debits − reversals", icon: Ticket, tint: "text-emerald-300" },
  ]), [summary]);

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {summaryCards.map(({ key, label, value, sub, icon: Icon, tint }) => (
          <div key={key} data-testid={`vouchers-summary-${key}`} className="velo-surface rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
              <Icon className={`w-4 h-4 ${tint}`} strokeWidth={1.75} />
            </div>
            <div className="text-2xl font-light text-white font-mono mt-2 tabular-nums">{(value || 0).toLocaleString("en-IN")}</div>
            <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="velo-surface rounded-lg p-4 border border-white/10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="lg:col-span-2">
            <Label className="text-slate-300 text-[11px] uppercase tracking-wider">Search user / reference</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" strokeWidth={2} />
              <Input
                data-testid="input-vouchers-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name, phone or reference id"
                className="velo-input pl-9 h-10"
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-[11px] uppercase tracking-wider">Type</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="select-vouchers-type" className="velo-input h-10 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#22103F] border-white/10 text-white">
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="debit">Debits (purchases)</SelectItem>
                <SelectItem value="reverse">Reversals only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-[11px] uppercase tracking-wider">From</Label>
            <Input data-testid="input-vouchers-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="velo-input h-10 mt-1.5" />
          </div>
          <div>
            <Label className="text-slate-300 text-[11px] uppercase tracking-wider">To</Label>
            <Input data-testid="input-vouchers-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="velo-input h-10 mt-1.5" />
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3 mt-4 pt-4 border-t border-white/5">
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input
              type="checkbox"
              data-testid="checkbox-reversed-only"
              checked={reversedOnly}
              onChange={(e) => setReversedOnly(e.target.checked)}
              className="rounded border-white/20 bg-transparent accent-pink-500 w-4 h-4"
            />
            Show only reversed debits
          </label>
          <Button
            data-testid="btn-vouchers-export"
            onClick={exportCsv}
            size="sm"
            variant="outline"
            className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/5 h-9"
          >
            <Download className="w-3.5 h-3.5 mr-2" strokeWidth={1.75} /> Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="velo-card overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-[#170929] border-b border-white/10">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-6 py-4">User</th>
              <th className="px-6 py-4">Reference</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4 text-right">Coins</th>
              <th className="px-6 py-4 text-right">Balance after</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`voucher-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                <td className="px-6 py-4 text-sm">
                  <div className="text-white">{r.user_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {r.user_phone || "—"}{r.user_role ? ` · ${r.user_role}` : ""}
                  </div>
                </td>
                <td className="px-6 py-4 text-xs font-mono text-pink-400 break-all max-w-[200px]">{r.reference_id || "—"}</td>
                <td className="px-6 py-4">
                  {r.type === "debit" ? (
                    <Badge className="bg-pink-500/20 text-pink-300 border-pink-500/30 border capitalize">Debit</Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 border capitalize">Reversal</Badge>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-right font-mono text-white tabular-nums">
                  {r.type === "reverse" ? `+${r.amount}` : `−${r.amount}`}
                </td>
                <td className="px-6 py-4 text-sm text-right font-mono text-slate-300 tabular-nums">{r.points_after ?? "—"}</td>
                <td className="px-6 py-4">
                  {r.reversed ? (
                    <Badge className="bg-red-500/20 text-red-300 border-red-500/30 border">Reversed</Badge>
                  ) : (
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">Settled</Badge>
                  )}
                </td>
                <td className="px-6 py-4 text-xs text-slate-500">
                  {r.created_at ? format(new Date(r.created_at), "dd MMM yy, HH:mm") : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-slate-500 text-sm">
                  {loading ? "Loading voucher redemptions…" : "No voucher redemptions yet"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pager
          total={total}
          limit={limit}
          skip={skip}
          testidPrefix="pager-vouchers"
          onChange={({ limit: l, skip: s }) => { setLimit(l); setSkip(s); }}
        />
      </div>
    </div>
  );
}
